"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { Components } from "@/app/(components)/_components/Components";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import { ModalStore } from "@/app/stores/ModalStore";
import { OfferStore } from "@/app/stores/OfferStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { ProjectOfferData } from "@/app/types/client-avatar/UserClientAvatar";
import { PageParams } from "@/app/types/common/DefaultProps";
import { URLs } from "@/app/utils/URLs";
import { Limits } from "@/app/vars/Limits";
import { QueryKey } from "@/app/vars/QueryKeys";
import { Routes } from "@/app/vars/Routes";

export type OfferActions = {
  add: () => void;
  duplicate: (offer: ProjectOfferData) => void;
  remove: (offer: ProjectOfferData) => void;
};

export function useOfferActions(params: PageParams): OfferActions {
  const T = useT();
  const queryClient = useQueryClient();

  const projectId = ProjectStore((state) => state.currentProject?.id);
  const refreshOffers = OfferStore((state) => state.refreshOffers);

  const invalidate = useCallback(() => {
    if (!projectId) {
      return;
    }

    queryClient.invalidateQueries({ queryKey: [QueryKey.PROJECT_OFFER, projectId] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.PROJECT_WORKSPACE] });
    refreshOffers(projectId);
  }, [projectId, queryClient, refreshOffers]);

  const openCreated = useCallback(
    (created?: ProjectOfferData) => {
      if (!created) {
        invalidate();
        return;
      }

      OfferStore.getState().addOffer(created);
      window.location.assign(URLs.pathOf(Routes.PROTECTED.OFFER(created.uuid)));

      return false;
    },
    [invalidate],
  );

  const add = useCallback(() => {
    if (!projectId) {
      return;
    }

    ModalStore.getState().openModal(
      Components.OfferForm,
      T(TKey.PROJECT_OFFER_ADD) as string,
      { params, projectId, isCreating: true, onAfterSave: openCreated },
      ModalType.MEDIUM,
    );
  }, [params, projectId, openCreated, T]);

  const duplicate = useCallback(
    (offer: ProjectOfferData) => {
      if (!projectId) {
        return;
      }

      const name = (T(TKey.OFFER_DUPLICATE_NAME) as string)
        .replace("{name}", offer.name)
        .slice(0, Limits.MAX_OFFER_NAME);

      ModalStore.getState().openModal(
        Components.OfferForm,
        T(TKey.OFFER_DUPLICATE_TITLE) as string,
        {
          params,
          projectId,
          offer: { ...offer, name },
          isCreating: true,
          onAfterSave: openCreated,
        },
        ModalType.MEDIUM,
      );
    },
    [params, projectId, openCreated, T],
  );

  const remove = useCallback(
    (offer: ProjectOfferData) => {
      if (!projectId) {
        return;
      }

      ModalStore.getState().openModal(
        Components.OfferDeletion,
        T(TKey.OFFER_DELETE_TITLE) as string,
        { params, projectId, offer, onDeleted: invalidate },
        ModalType.SMALL,
      );
    },
    [params, projectId, invalidate, T],
  );

  return { add, duplicate, remove };
}
